import { useState } from "react";
import { Check, X, Rocket, ChevronDown } from "lucide-react";

export const OnboardingChecklist = ({ user, stats, projects, teams, onChangeView, onNewTask }) => {
  const uid = user?.user_id || "demo";
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(`opero_checklist_dismissed_${uid}`) === "1");
  const [collapsed, setCollapsed] = useState(false);
  const [visited, setVisited] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`opero_checklist_visited_${uid}`)) || {}; } catch { return {}; }
  });

  const markVisited = (key) => {
    const next = { ...visited, [key]: true };
    setVisited(next);
    localStorage.setItem(`opero_checklist_visited_${uid}`, JSON.stringify(next));
  };

  const steps = [
    { key: "task", label: "Create your first task", done: (stats?.total || 0) > 0, action: onNewTask },
    { key: "project", label: "Add a project", done: (projects?.length || 0) > 0, action: () => onChangeView("projects") },
    { key: "team", label: "Create a team & invite someone", done: (teams?.length || 0) > 0, action: () => onChangeView("teams") },
    { key: "dashboard", label: "Check your analytics dashboard", done: !!visited.dashboard, action: () => { markVisited("dashboard"); onChangeView("dashboard"); } },
    { key: "chat", label: "Say hi in Team Chat", done: !!visited.chat, action: () => { markVisited("chat"); onChangeView("chat"); } },
  ];
  const doneCount = steps.filter((s) => s.done).length;

  if (dismissed || doneCount === steps.length) return null;

  return (
    <div className="mx-6 mt-4 bg-card border rounded-2xl p-4 shadow-sm" data-testid="onboarding-checklist">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><Rocket className="h-4.5 w-4.5 text-primary" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">Getting started</span>
            <span className="text-xs text-muted-foreground">{doneCount}/{steps.length} done</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full mt-1.5 overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${(doneCount / steps.length) * 100}%` }} />
          </div>
        </div>
        <button onClick={() => setCollapsed(!collapsed)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground" data-testid="checklist-collapse-btn">
          <ChevronDown className={`h-4 w-4 transition-transform ${collapsed ? "" : "rotate-180"}`} />
        </button>
        <button onClick={() => { localStorage.setItem(`opero_checklist_dismissed_${uid}`, "1"); setDismissed(true); }} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground" data-testid="checklist-dismiss-btn">
          <X className="h-4 w-4" />
        </button>
      </div>
      {!collapsed && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2 mt-3">
          {steps.map((s) => (
            <button key={s.key} onClick={s.done ? undefined : s.action} disabled={s.done}
              className={`flex items-center gap-2 text-left text-xs rounded-lg px-3 py-2.5 border transition-colors ${s.done ? "bg-emerald-500/5 border-emerald-500/20 text-muted-foreground" : "bg-background border-border hover:border-primary/40 hover:bg-primary/5"}`}
              data-testid={`checklist-step-${s.key}`}>
              <span className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${s.done ? "bg-emerald-500 text-white" : "border border-muted-foreground/40"}`}>
                {s.done && <Check className="h-3 w-3" />}
              </span>
              <span className={s.done ? "line-through" : "font-medium"}>{s.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
